import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  LayoutDashboard, 
  Users, 
  TrendingUp, 
  Target, 
  FileText, 
  MessageSquare, 
  FormInput, 
  UserCheck, 
  BarChart3, 
  CheckSquare, 
  Settings, 
  Search, 
  Bell, 
  LogOut, 
  Plus, 
  Filter, 
  Download, 
  Eye, 
  EyeOff, 
  ChevronRight, 
  Globe, 
  Smartphone, 
  Monitor, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  ArrowUpRight, 
  MoreVertical, 
  Phone, 
  Mail, 
  Building2, 
  MapPin, 
  Calendar, 
  Sparkles, 
  DollarSign, 
  ArrowRight,
  ShieldCheck,
  Edit2,
  Copy,
  ExternalLink,
  Check,
  Zap,
  PieChart,
  Trash2,
  UserPlus,
  ToggleLeft,
  ToggleRight,
  RotateCcw
} from 'lucide-react';
import { getWaAdmins, saveWaAdmins } from '@/utils/waRotator';
import { getPixelConfig, savePixelConfig } from '@/utils/metaPixelTracker';
import { fetchMetaAdsCampaigns } from '@/utils/metaAdsApi';
import { trackCurrentVisitor, getRealtimeVisitors } from '@/utils/visitorTracker';
import LiveVisitorView from '@/components/identity/LiveVisitorView';
import ClientAccountsView from '@/components/identity/ClientAccountsView';
import ClientAccountDetailPage from '@/components/identity/ClientAccountDetailPage';
import ActiveSessionsView from '@/components/identity/ActiveSessionsView';
import AuditLogView from '@/components/identity/AuditLogView';
import VisitorHistoryView from '@/components/identity/VisitorHistoryView';

// -------------------------------------------------------------
// MOCK DATASETS (MATCHING MOCKUP SCREENSHOTS EXACTLY)
// -------------------------------------------------------------
const INITIAL_LEADS_DATA = [];

const INITIAL_VISITORS = [];

const INITIAL_CAMPAIGNS = [];

const INITIAL_FORMS = [];

const INITIAL_LANDING_PAGES = [
  { id: 1, title: 'Maklon Kosmetik & Skincare', path: '/layanan/maklon-kosmetik', status: 'Aktif', views: 0, leads: 0, conv: '0.00%' },
  { id: 2, title: 'Kalkulator Pricing Parfum', path: '/pricing/parfum', status: 'Aktif', views: 0, leads: 0, conv: '0.00%' },
  { id: 3, title: 'Kalkulator Pricing Skincare', path: '/pricing/skincare', status: 'Aktif', views: 0, leads: 0, conv: '0.00%' },
  { id: 4, title: 'Layanan Formulasi Parfumer', path: '/layanan/formulasi', status: 'Aktif', views: 0, leads: 0, conv: '0.00%' },
  { id: 5, title: 'Landing Page Promo Ramadan', path: '/produk/parfum', status: 'Draft', views: 0, leads: 0, conv: '0.00%' }
];

const INITIAL_TASKS = [];

const WA_TEMPLATES = [
  {
    id: 1,
    name: 'Sapaan Lead Baru',
    category: 'Initial Contact',
    text: 'Halo kak {nama}, terima kasih telah menghubungi Satala Dermatech Essential! Saya {pic} dari tim konsultan maklon. Kami melihat Anda tertarik dengan {paket}. Kapan waktu yang tepat untuk kita berdiskusi?'
  },
  {
    id: 2,
    name: 'Follow Up H+1 Negosiasi',
    category: 'Follow Up',
    text: 'Halo kak {nama}, melanjutkan diskusi kemarin mengenai rencana maklon brand {perusahaan}, apakah ada pertanyaan seputar sampel aroma/formulasi yang bisa kami bantu?'
  },
  {
    id: 3,
    name: 'Penawaran Promo MOQ',
    category: 'Promo',
    text: 'Halo kak {nama}, ada kabar gembira! Khusus pemesanan bulan ini untuk MOQ {moq}, Satala memberikan subsidi gratis biaya perizinan legalitas BPOM & HAKI.'
  },
  {
    id: 4,
    name: 'Konfirmasi Pengiriman SPH',
    category: 'Closing',
    text: 'Halo kak {nama}, draft SPH (Surat Penawaran Harga) untuk {paket} sudah selesai kami susun dan dikirimkan ke email {email}. Mohon dicek ya kak!'
  }
];

export default function MarketingPortalControlPage() {
  // Authentication State
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('satala_marketing_auth') === 'true';
  });

  // Login Form State
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Active Menu / Page View
  const [activeMenu, setActiveMenu] = useState('live-visitors'); // Default to Live Visitor spec
  const [selectedLead, setSelectedLead] = useState(INITIAL_LEADS_DATA[0]);
  const [selectedClientForDetail, setSelectedClientForDetail] = useState(null);

  // Global Search State
  const [globalSearch, setGlobalSearch] = useState('');

  // Lists & Data States
  const [leadsList, setLeadsList] = useState(() => {
    try {
      const saved = localStorage.getItem('satala_leads_data');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return [];
  });

  useEffect(() => {
    const handleLeadsChanged = () => {
      try {
        const saved = localStorage.getItem('satala_leads_data');
        if (saved) setLeadsList(JSON.parse(saved));
      } catch (e) {}
    };
    window.addEventListener('satala_leads_changed', handleLeadsChanged);
    return () => window.removeEventListener('satala_leads_changed', handleLeadsChanged);
  }, []);

  const [leadsSearch, setLeadsSearch] = useState('');
  const [leadStatusFilter, setLeadStatusFilter] = useState('ALL');
  const [leadSourceFilter, setLeadSourceFilter] = useState('ALL');
  const [visitorTabFilter, setVisitorTabFilter] = useState('ALL'); // 'ALL', 'VERIFIED', 'ANONYMOUS'

  // Filtered Leads based on search query, status filter, and traffic source filter
  const filteredLeads = useMemo(() => {
    return leadsList.filter(lead => {
      const matchesSearch = 
        (lead.name || '').toLowerCase().includes(leadsSearch.toLowerCase()) ||
        (lead.phone || '').toLowerCase().includes(leadsSearch.toLowerCase()) ||
        (lead.email || '').toLowerCase().includes(leadsSearch.toLowerCase()) ||
        (lead.source || '').toLowerCase().includes(leadsSearch.toLowerCase());
      
      const matchesStatus = leadStatusFilter === 'ALL' || lead.status === leadStatusFilter;
      const matchesSource = leadSourceFilter === 'ALL' || lead.source === leadSourceFilter;
      return matchesSearch && matchesStatus && matchesSource;
    });
  }, [leadsList, leadsSearch, leadStatusFilter, leadSourceFilter]);
  
  const [landingPages, setLandingPages] = useState(INITIAL_LANDING_PAGES);
  const [tasks, setTasks] = useState(INITIAL_TASKS);
  
  // Modal State for Add Lead
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    source: 'Google Ads',
    pic: 'Fadli',
    package: 'Parfum Signature 50ml',
    moq: '1.000 Pcs',
    estOrder: 'Mei 2026'
  });

  // New Task Form State
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskPic, setNewTaskPic] = useState('Fadli');
  const [newTaskPriority, setNewTaskPriority] = useState('Medium');

  // Dynamic Campaigns State
  const [campaignsList, setCampaignsList] = useState(() => {
    const freshWebCampaign = [
      {
        id: 'meta_c3',
        name: 'Prospek Web 06/08/2026',
        setIklan: 'Set Iklan Prospek Web',
        channel: 'Meta Ads (FB/IG)',
        status: 'Aktif',
        budget: 50000,
        reach: 0,
        impressions: 0,
        frequency: '0.00',
        results: 0,
        spent: 0,
        cpr: 0,
        cpc: 0,
        cpm: 0,
        ctr: '0.00%'
      }
    ];
    try {
      const saved = localStorage.getItem('satala_campaigns_data');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map(c => {
            if (c.name.includes('Prospek') || c.name.includes('06/08')) {
              return { ...c, setIklan: 'Set Iklan Prospek Web', reach: 0, impressions: 0, frequency: '0.00', results: 0, spent: 0, cpr: 0, cpc: 0, cpm: 0, ctr: '0.00%' };
            }
            return c;
          });
        }
      }
    } catch (e) {}
    return freshWebCampaign;
  });
  const [isAddCampaignOpen, setIsAddCampaignOpen] = useState(false);
  const [newCampaignForm, setNewCampaignForm] = useState({
    name: '',
    channel: 'Meta Ads (FB/IG)',
    budget: '50000',
    spent: '0',
    status: 'Aktif'
  });

  // Modal State for Closing Deal Amount
  const [closingModalLead, setClosingModalLead] = useState(null);
  const [closingInvoiceAmount, setClosingInvoiceAmount] = useState('');

  // Dynamic Financial & Performance Calculations (Matching Wonyard Meta Ads Report Deck)
  const totalSpentAds = campaignsList.reduce((acc, c) => acc + (Number(c.spent) || 0), 0);
  const totalImpressions = campaignsList.reduce((acc, c) => acc + (Number(c.impressions) || 0), 0);
  const totalReach = campaignsList.reduce((acc, c) => acc + (Number(c.reach) || 0), 0);
  const totalLinkClicks = campaignsList.reduce((acc, c) => acc + (Number(c.results) || 0), 0);

  const closingLeads = useMemo(() => leadsList.filter(l => l.status === 'Closing'), [leadsList]);
  const totalClosingRevenue = useMemo(() => closingLeads.reduce((acc, l) => acc + (Number(l.dealAmount) || 0), 0), [closingLeads]);
  const dealCount = closingLeads.length;

  const merRoas = totalSpentAds > 0 ? (totalClosingRevenue / totalSpentAds).toFixed(2) : '0.00';
  const averageDeal = dealCount > 0 ? Math.round(totalClosingRevenue / dealCount) : 0;
  const currentCPL = leadsList.length > 0 && totalSpentAds > 0 ? Math.round(totalSpentAds / leadsList.length) : 0;
  const cpaCpp = dealCount > 0 && totalSpentAds > 0 ? Math.round(totalSpentAds / dealCount) : 0;

  // Funnel Stage Percentages
  const reachPercentage = totalImpressions > 0 ? ((totalReach / totalImpressions) * 100).toFixed(1) : '0.0';
  const linkClickPercentage = totalReach > 0 ? ((totalLinkClicks / totalReach) * 100).toFixed(1) : '0.0';
  const leadPercentage = totalLinkClicks > 0 ? ((leadsList.length / totalLinkClicks) * 100).toFixed(1) : '0.0';
  const dealPercentage = leadsList.length > 0 ? ((dealCount / leadsList.length) * 100).toFixed(1) : '0.0';
  const leadToDealRatio = dealCount > 0 ? `${Math.round(leadsList.length / dealCount)}:1` : '0:1';

  // Sorted Top Clients by Revenue
  const topClients = useMemo(() => {
    return [...closingLeads]
      .sort((a, b) => (Number(b.dealAmount) || 0) - (Number(a.dealAmount) || 0))
      .map((lead, idx) => {
        const rev = Number(lead.dealAmount) || 0;
        const contrib = totalClosingRevenue > 0 ? ((rev / totalClosingRevenue) * 100).toFixed(1) : '0.0';
        return {
          rank: idx + 1,
          id: lead.id,
          name: lead.name,
          package: lead.package || 'Maklon Kosmetik',
          revenue: rev,
          contribution: contrib,
          source: lead.source || 'Meta Ads'
        };
      });
  }, [closingLeads, totalClosingRevenue]);

  // Realtime Visitors Tracking State
  const [realtimeVisitors, setRealtimeVisitors] = useState([]);

  useEffect(() => {
    trackCurrentVisitor('Digital Marketing Control', { id: 'fadli_admin', name: 'Fadli (Marketing Team)', email: 'fadli@satala.co.id' });
    
    const refreshVisitors = async () => {
      const visitors = await getRealtimeVisitors();
      if (Array.isArray(visitors)) {
        setRealtimeVisitors(visitors);
      }
    };

    refreshVisitors();

    const handleVisitorsChanged = () => {
      refreshVisitors();
    };
    window.addEventListener('satala_visitors_changed', handleVisitorsChanged);
    const interval = setInterval(refreshVisitors, 3000);
    return () => {
      window.removeEventListener('satala_visitors_changed', handleVisitorsChanged);
      clearInterval(interval);
    };
  }, []);

  // Reset All Leads & WA Chat Counter
  const handleResetAllData = () => {
    if (window.confirm('Apakah Anda yakin ingin menghapus SEMUA data leads dan me-reset hitungan chat WA ke 0?')) {
      setLeadsList([]);
      localStorage.removeItem('satala_leads_data');
      window.dispatchEvent(new Event('satala_leads_changed'));

      const resetAdmins = waAdmins.map(a => ({ ...a, leadsCount: 0 }));
      setWaAdmins(resetAdmins);
      saveWaAdmins(resetAdmins);

      toast.success('⚡ Semua data leads & hitungan chat WA berhasil di-reset ke 0!');
    }
  };

  // Pixel Settings State
  const [pixelConfig, setPixelConfig] = useState(getPixelConfig);

  // WA Admins Rotator State
  const [waAdmins, setWaAdmins] = useState(getWaAdmins);
  const [isAddAdminOpen, setIsAddAdminOpen] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({
    name: '',
    phone: '',
    role: 'Admin Sales'
  });

  const handleToggleAdminActive = (id) => {
    const updated = waAdmins.map(a => a.id === id ? { ...a, active: !a.active } : a);
    setWaAdmins(updated);
    saveWaAdmins(updated);
    const toggled = updated.find(a => a.id === id);
    toast.success(`Status ${toggled.name} diubah menjadi ${toggled.active ? 'AKTIF' : 'NONAKTIF'}`);
  };

  const handleCreateAdmin = (e) => {
    e.preventDefault();
    if (!newAdminForm.name || !newAdminForm.phone) {
      toast.error('Harap isi Nama Admin dan Nomor WhatsApp!');
      return;
    }
    const created = {
      id: Date.now(),
      name: newAdminForm.name.trim(),
      phone: newAdminForm.phone.trim(),
      role: newAdminForm.role.trim() || 'Admin Sales',
      active: true,
      leadsCount: 0
    };
    const updated = [...waAdmins, created];
    setWaAdmins(updated);
    saveWaAdmins(updated);
    setIsAddAdminOpen(false);
    setNewAdminForm({ name: '', phone: '', role: 'Admin Sales' });
    toast.success(`Admin WA baru "${created.name}" berhasil ditambahkan!`);
  };

  const [editingAdmin, setEditingAdmin] = useState(null);

  const handleUpdateAdmin = (e) => {
    e.preventDefault();
    if (!editingAdmin || !editingAdmin.name || !editingAdmin.phone) {
      toast.error('Harap isi Nama Admin dan Nomor WhatsApp!');
      return;
    }
    const updated = waAdmins.map(a => a.id === editingAdmin.id ? { ...editingAdmin } : a);
    setWaAdmins(updated);
    saveWaAdmins(updated);
    setEditingAdmin(null);
    toast.success(`Data Admin WA "${editingAdmin.name}" berhasil diperbarui!`);
  };

  const handleDeleteAdmin = (id) => {
    const admin = waAdmins.find(a => a.id === id);
    const updated = waAdmins.filter(a => a.id !== id);
    setWaAdmins(updated);
    saveWaAdmins(updated);
    toast.info(`Admin WA ${admin?.name || ''} berhasil dihapus.`);
  };

  // Handle Login
  const handleLogin = (e) => {
    e.preventDefault();
    if (!loginEmail) {
      setLoginError('Masukkan username atau email.');
      return;
    }
    if (!loginPassword) {
      setLoginError('Masukkan password.');
      return;
    }

    if (loginPassword.length >= 4) {
      sessionStorage.setItem('satala_marketing_auth', 'true');
      setIsAuthenticated(true);
      setLoginError('');
      toast.success('Selamat datang kembali, Lead Digital Marketer!');
    } else {
      setLoginError('Password minimal 4 karakter.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('satala_marketing_auth');
    setIsAuthenticated(false);
    toast.info('Sesi Marketing Control ditutup.');
  };

  const openLeadDetail = (lead) => {
    setSelectedLead(lead);
    setActiveMenu('lead-detail');
  };

  const saveLeadsList = (updated) => {
    setLeadsList(updated);
    localStorage.setItem('satala_leads_data', JSON.stringify(updated));
  };

  // Add New Lead Submission
  const handleCreateLead = (e) => {
    e.preventDefault();
    if (!newLead.name || !newLead.phone) {
      toast.error('Harap isi Nama dan Nomor WhatsApp!');
      return;
    }

    const createdItem = {
      id: Date.now(),
      ...newLead,
      date: new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' + new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }),
      status: 'Belum Follow Up',
      utm: `utm_source=${(newLead.source || '').toLowerCase().replace(/\s+/g, '')}&utm_medium=manual`
    };

    saveLeadsList([createdItem, ...leadsList]);
    setIsAddLeadOpen(false);
    setNewLead({
      name: '',
      email: '',
      phone: '',
      city: '',
      source: 'Google Ads',
      pic: 'Fadli',
      package: 'Parfum Signature 50ml',
      moq: '1.000 Pcs',
      estOrder: 'Mei 2026'
    });
    toast.success(`Lead baru "${createdItem.name}" berhasil ditambahkan!`);
  };

  // Update Lead Status
  const handleUpdateLeadStatus = (id, newStatus) => {
    if (newStatus === 'Closing') {
      const targetLead = leadsList.find(l => l.id === id);
      setClosingModalLead(targetLead || null);
      setClosingInvoiceAmount(targetLead?.dealAmount ? String(targetLead.dealAmount) : '');
      return;
    }
    const updated = leadsList.map(l => l.id === id ? { ...l, status: newStatus } : l);
    saveLeadsList(updated);
    if (selectedLead && selectedLead.id === id) {
      setSelectedLead(prev => ({ ...prev, status: newStatus }));
    }
    toast.success(`Status lead berhasil diubah ke: ${newStatus}`);
  };

  const handleSaveClosingDeal = (e) => {
    e.preventDefault();
    if (!closingModalLead) return;
    const amount = Number(closingInvoiceAmount) || 0;
    const updated = leadsList.map(l => l.id === closingModalLead.id ? { ...l, status: 'Closing', dealAmount: amount } : l);
    saveLeadsList(updated);
    if (selectedLead && selectedLead.id === closingModalLead.id) {
      setSelectedLead(prev => ({ ...prev, status: 'Closing', dealAmount: amount }));
    }
    toast.success(`🎉 Selamat! Klien "${closingModalLead.name}" Closing sebesar Rp ${amount.toLocaleString('id-ID')}! ROAS diperbarui!`);
    setClosingModalLead(null);
    setClosingInvoiceAmount('');
  };

  const handleCreateCampaign = (e) => {
    e.preventDefault();
    if (!newCampaignForm.name) {
      toast.error('Harap isi Nama Campaign!');
      return;
    }
    const created = {
      id: Date.now(),
      name: newCampaignForm.name.trim(),
      channel: newCampaignForm.channel,
      budget: Number(newCampaignForm.budget) || 50000,
      spent: Number(newCampaignForm.spent) || 0,
      status: newCampaignForm.status
    };
    const updated = [created, ...campaignsList];
    setCampaignsList(updated);
    localStorage.setItem('satala_campaigns_data', JSON.stringify(updated));
    setIsAddCampaignOpen(false);
    setNewCampaignForm({ name: '', channel: 'Meta Ads (FB/IG)', budget: '50000', spent: '0', status: 'Aktif' });
    toast.success(`Campaign baru "${created.name}" berhasil dibuat!`);
  };

  const handleDeleteCampaign = (id) => {
    const updated = campaignsList.filter(c => c.id !== id);
    setCampaignsList(updated);
    localStorage.setItem('satala_campaigns_data', JSON.stringify(updated));
    try {
      const ignored = JSON.parse(localStorage.getItem('satala_ignored_campaigns') || '[]');
      if (!ignored.includes(id)) {
        ignored.push(id);
        localStorage.setItem('satala_ignored_campaigns', JSON.stringify(ignored));
      }
    } catch (e) {}
    toast.info('Campaign dihapus & diabaikan dari pemantauan.');
  };

  const handleUpdateCampaignSpent = (id, newSpent) => {
    const updated = campaignsList.map(c => c.id === id ? { ...c, spent: Number(newSpent) || 0 } : c);
    setCampaignsList(updated);
    localStorage.setItem('satala_campaigns_data', JSON.stringify(updated));
    toast.success('Biaya spent campaign diperbarui!');
  };

  const [isSyncingMeta, setIsSyncingMeta] = useState(false);

  const handleSyncMetaAds = async (showToast = true) => {
    setIsSyncingMeta(true);
    if (showToast) {
      toast.loading('Menghubungi Meta Ads Manager API...', { id: 'meta-sync' });
    }
    try {
      const fetched = await fetchMetaAdsCampaigns(pixelConfig.metaAdAccountId || '779514711733398', pixelConfig.metaAccessToken || '');
      const ignored = JSON.parse(localStorage.getItem('satala_ignored_campaigns') || '[]');
      const filtered = fetched.filter(c => !ignored.includes(c.id));
      setCampaignsList(filtered);
      localStorage.setItem('satala_campaigns_data', JSON.stringify(filtered));
      if (showToast) {
        toast.success(`⚡ Berhasil sinkronkan ${filtered.length} campaign realtime dari Meta Ads!`, { id: 'meta-sync' });
      }
    } catch (err) {
      if (showToast) {
        toast.error(`Gagal sinkronkan Meta Ads: ${err.message}`, { id: 'meta-sync' });
      }
    } finally {
      setIsSyncingMeta(false);
    }
  };

  // Auto-Sync Meta Ads when navigating to Campaigns tab
  useEffect(() => {
    if (activeMenu === 'campaigns') {
      handleSyncMetaAds(false);
    }
  }, [activeMenu]);

  // Toggle Landing Page Status
  const handleToggleLandingPage = (id) => {
    setLandingPages(prev => prev.map(lp => lp.id === id ? { ...lp, status: lp.status === 'Aktif' ? 'Draft' : 'Aktif' } : lp));
    toast.info('Status Landing Page diperbarui.');
  };

  // Task Actions
  const handleToggleTask = (id) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
  };

  const handleAddTask = (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    const taskObj = {
      id: Date.now(),
      title: newTaskTitle.trim(),
      priority: newTaskPriority,
      completed: false,
      due: 'Hari ini',
      pic: newTaskPic
    };
    setTasks([taskObj, ...tasks]);
    setNewTaskTitle('');
    toast.success('Tugas baru berhasil ditambahkan!');
  };

  // Copy helper
  const handleCopyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Teks berhasil disalin ke clipboard!');
  };

  // -------------------------------------------------------------
  // RENDER 1: LOGIN GATEWAY (MATCHES USER MOCKUP SCREENSHOT)
  // -------------------------------------------------------------
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] text-slate-800 flex flex-col items-center justify-center p-4 font-sans relative">
        <div className="w-full max-w-md text-center mb-6">
          <img 
            src="/logo.png" 
            alt="Satala Dermatech Essential" 
            className="h-16 object-contain mx-auto mb-3" 
          />
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Marketing Control</h1>
          <p className="text-xs text-slate-500 mt-1">Digital Marketing Dashboard & Analytics</p>
        </div>

        <div className="w-full max-w-md bg-white border border-slate-200/80 rounded-2xl p-6 sm:p-8 shadow-sm">
          <form onSubmit={handleLogin} className="space-y-4 text-xs">
            {loginError && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-600 rounded-xl text-xs font-semibold text-center animate-in fade-in">
                {loginError}
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-slate-700 font-semibold text-xs">Username</Label>
              <Input
                type="text"
                required
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                placeholder="Masukkan username"
                className="h-11 bg-white border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl text-xs focus:ring-slate-800"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-700 font-semibold text-xs">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  required
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  placeholder="Masukkan password"
                  className="h-11 bg-white border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl text-xs pr-10 focus:ring-slate-800"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-[#18181b] hover:bg-black text-white font-bold rounded-xl text-xs shadow-sm mt-2 transition-all"
            >
              Login
            </Button>
          </form>
        </div>

        <p className="text-xs text-slate-400 text-center mt-8">
          © 2026 Satala Dermatech Essential. All rights reserved.
        </p>
      </div>
    );
  }

  // -------------------------------------------------------------
  // RENDER 2: MAIN MARKETING CONTROL PORTAL
  // -------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#f8f9fa] text-slate-800 font-sans flex flex-col md:flex-row">
      
      {/* ─── SIDEBAR NAVIGATION (LEFT) ─── */}
      <aside className="w-full md:w-64 bg-white border-r border-slate-200/80 flex flex-col justify-between shrink-0">
        <div>
          {/* Logo & Brand Header */}
          <div className="p-5 border-b border-slate-100 flex items-center gap-3">
            <img src="/logo.png" alt="Satala Dermatech Essential" className="h-10 object-contain" />
            <div>
              <h2 className="font-extrabold text-slate-900 text-sm tracking-tight leading-none">SATALA</h2>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Dermatech Essential</p>
            </div>
          </div>

          {/* Navigation Menu (Grouped SaaS Structure) */}
          <nav className="p-3 space-y-4 text-xs font-semibold overflow-y-auto">
            {[
              {
                title: 'VISITOR ANALYTICS',
                items: [
                  { id: 'live-visitors', label: 'Live Visitor', icon: UserCheck },
                  { id: 'visitor-history', label: 'Visitor History', icon: Clock }
                ]
              },
              {
                title: 'IDENTITY CENTER',
                items: [
                  { id: 'client-accounts', label: 'Client Accounts', icon: Users },
                  { id: 'admin-accounts', label: 'Admin Accounts', icon: ShieldCheck },
                  { id: 'active-sessions', label: 'Active Sessions', icon: Monitor },
                  { id: 'audit-logs', label: 'Audit Log', icon: FileText }
                ]
              },
              {
                title: 'MARKETING & SALES',
                items: [
                  { id: 'dashboard', label: 'Executive Summary', icon: LayoutDashboard },
                  { id: 'funnel', label: 'Marketing Funnel', icon: TrendingUp },
                  { id: 'leads', label: 'Leads & Closing CRM', icon: Users },
                  { id: 'top-clients', label: 'Ranking Top Client', icon: BarChart3 },
                  { id: 'campaigns', label: 'Campaigns Iklan', icon: Target }
                ]
              },
              {
                title: 'SUPPORT & TOOLS',
                items: [
                  { id: 'whatsapp', label: 'WhatsApp CS Rotator', icon: MessageSquare },
                  { id: 'settings', label: 'Pengaturan & Pixel', icon: Settings }
                ]
              }
            ].map((section, sIdx) => (
              <div key={sIdx} className="space-y-1">
                <p className="px-3.5 text-[9px] font-extrabold uppercase tracking-widest text-[#A97838] opacity-80 mb-1">
                  {section.title}
                </p>
                {section.items.map(item => {
                  const IconComponent = item.icon;
                  const isActive = activeMenu === item.id || 
                    (item.id === 'live-visitors' && activeMenu === 'visitors') ||
                    (item.id === 'client-accounts' && activeMenu === 'client-detail') ||
                    (item.id === 'leads' && activeMenu === 'lead-detail');
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveMenu(item.id)}
                      className={`w-full flex items-center gap-3 px-3.5 py-2 rounded-xl transition-all ${
                        isActive 
                          ? 'bg-[#F4EFE6] text-[#A97838] font-bold shadow-2xs' 
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                    >
                      <IconComponent className={`w-4 h-4 ${isActive ? 'text-[#A97838]' : 'text-slate-400'}`} />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
        </div>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-slate-100 space-y-3">
          <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100 text-[10px] text-slate-500 font-medium flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0" />
            <div>
              <p className="font-bold text-slate-700">Marketing Control</p>
              <p className="text-[9px]">Internal Use Only</p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-rose-600 px-3 py-2 rounded-xl transition-colors"
          >
            <LogOut className="w-4 h-4 text-rose-500" /> Logout
          </button>
        </div>
      </aside>

      {/* ─── MAIN CONTENT AREA ─── */}
      <main className="flex-1 overflow-y-auto pb-16">
        
        {/* Top Navigation Bar */}
        <header className="bg-white border-b border-slate-200/80 px-6 py-3.5 flex justify-between items-center sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <h2 className="font-bold text-slate-900 text-base capitalize">{(activeMenu || '').replace('-', ' ')}</h2>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative hidden sm:block w-72">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cari data, leads, campaign..."
                value={globalSearch}
                onChange={e => setGlobalSearch(e.target.value)}
                className="pl-9 h-9 bg-slate-50 border-slate-200 text-xs rounded-xl"
              />
            </div>

            <button onClick={() => toast.info('Tidak ada notifikasi baru.')} className="relative p-2 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors">
              <Bell className="w-4 h-4" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full"></span>
            </button>

            <div className="flex items-center gap-2 pl-2 border-l border-slate-200">
              <div className="w-8 h-8 rounded-full bg-slate-900 text-white font-bold text-xs flex items-center justify-center">
                F
              </div>
              <div className="text-left hidden lg:block">
                <p className="text-xs font-bold text-slate-800 leading-tight">Fadli</p>
                <p className="text-[10px] text-slate-500">Marketing Team</p>
              </div>
            </div>
          </div>
        </header>

        {/* ─── ENTERPRISE IDENTITY & VISITOR MANAGEMENT SUITE VIEWS ─── */}
        {(activeMenu === 'live-visitors' || activeMenu === 'visitors') && (
          <LiveVisitorView globalSearch={globalSearch} />
        )}

        {activeMenu === 'visitor-history' && (
          <VisitorHistoryView globalSearch={globalSearch} />
        )}

        {activeMenu === 'client-accounts' && (
          <ClientAccountsView 
            globalSearch={globalSearch} 
            onSelectClientDetail={(client) => {
              setSelectedClientForDetail(client);
              setActiveMenu('client-detail');
            }} 
          />
        )}

        {activeMenu === 'client-detail' && (
          <ClientAccountDetailPage 
            client={selectedClientForDetail} 
            onBack={() => setActiveMenu('client-accounts')} 
          />
        )}

        {activeMenu === 'active-sessions' && (
          <ActiveSessionsView globalSearch={globalSearch} />
        )}

        {activeMenu === 'audit-logs' && (
          <AuditLogView globalSearch={globalSearch} />
        )}

        {activeMenu === 'admin-accounts' && (
          <div className="p-6 space-y-4">
            <Card className="bg-white border-[#ECECEC] p-6">
              <h3 className="font-bold text-[#302621] text-sm mb-2">Admin Accounts & Team Permissions</h3>
              <p className="text-xs text-slate-500">Kelola akun internal tim marketing & office Satala Dermatech.</p>
            </Card>
          </div>
        )}

        {/* ─── VIEW 1: EXECUTIVE SUMMARY (WONYARD SLIDE 2 MATCH) ─── */}
        {activeMenu === 'dashboard' && (
          <div className="p-6 space-y-6">
            
            <div className="bg-gradient-to-r from-[#18181b] via-[#27272a] to-[#3f3f46] p-6 rounded-3xl text-white shadow-md flex justify-between items-center">
              <div>
                <span className="text-[10px] uppercase font-bold tracking-widest text-amber-400 bg-amber-400/10 px-2.5 py-1 rounded-full border border-amber-400/20">Executive Summary</span>
                <h2 className="text-2xl font-black mt-2 tracking-tight">Ringkasan Kinerja Meta Ads</h2>
                <p className="text-xs text-slate-300 mt-1">PT. Satala Dermatech Essential • Realtime Ad Performance & Sales Funnel Analytics</p>
              </div>
              <Button 
                onClick={() => setActiveMenu('funnel')}
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs h-10 rounded-xl gap-2 shadow-sm"
              >
                Lihat Funnel Visual <ArrowRight className="w-4 h-4" />
              </Button>
            </div>

            {/* 12 Executive KPI Cards Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              
              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-3.5">
                  <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">Total Budget</p>
                  <h3 className="text-sm font-black font-mono text-slate-900 mt-1">Rp {totalSpentAds.toLocaleString('id-ID')}</h3>
                  <p className="text-[9px] text-slate-500 mt-1">Biaya Iklan Terpakai</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-3.5">
                  <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">Impression</p>
                  <h3 className="text-sm font-black font-mono text-slate-900 mt-1">{totalImpressions.toLocaleString('id-ID')}</h3>
                  <p className="text-[9px] text-slate-500 mt-1">Total Tayangan Iklan</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-3.5">
                  <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">Reach</p>
                  <h3 className="text-sm font-black font-mono text-slate-900 mt-1">{totalReach.toLocaleString('id-ID')}</h3>
                  <p className="text-[9px] text-emerald-600 font-bold mt-1">{reachPercentage}% dari Impression</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-3.5">
                  <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">Link Click</p>
                  <h3 className="text-sm font-black font-mono text-slate-900 mt-1">{totalLinkClicks.toLocaleString('id-ID')}</h3>
                  <p className="text-[9px] text-emerald-600 font-bold mt-1">{linkClickPercentage}% dari Reach</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-3.5">
                  <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">CTR Link</p>
                  <h3 className="text-sm font-black font-mono text-blue-600 mt-1">{linkClickPercentage}%</h3>
                  <p className="text-[9px] text-slate-500 mt-1">Rasio Klik Tautan</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-3.5">
                  <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">CPC Link</p>
                  <h3 className="text-sm font-black font-mono text-slate-900 mt-1">
                    Rp {totalLinkClicks > 0 ? Math.round(totalSpentAds / totalLinkClicks).toLocaleString('id-ID') : 0}
                  </h3>
                  <p className="text-[9px] text-slate-500 mt-1">Biaya per Klik Web</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-3.5">
                  <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">Total Leads</p>
                  <h3 className="text-sm font-black font-mono text-slate-900 mt-1">{leadsList.length}</h3>
                  <p className="text-[9px] text-emerald-600 font-bold mt-1">Chat WA & Form</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-3.5">
                  <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">Deal Client</p>
                  <h3 className="text-sm font-black font-mono text-emerald-600 mt-1">{dealCount} Client</h3>
                  <p className="text-[9px] text-slate-500 mt-1">Klien Resmi Closing</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-3.5">
                  <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">Total Revenue</p>
                  <h3 className="text-sm font-black font-mono text-emerald-600 mt-1">Rp {totalClosingRevenue.toLocaleString('id-ID')}</h3>
                  <p className="text-[9px] text-emerald-600 font-bold mt-1">Total Hasil Invoice</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-3.5">
                  <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">MER (ROAS)</p>
                  <h3 className="text-base font-black font-mono text-amber-600 mt-1">{merRoas}x</h3>
                  <p className="text-[9px] text-slate-500 mt-1">Return On Ad Spend</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-3.5">
                  <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">Average Deal</p>
                  <h3 className="text-sm font-black font-mono text-slate-900 mt-1">Rp {averageDeal.toLocaleString('id-ID')}</h3>
                  <p className="text-[9px] text-slate-500 mt-1">Rata-rata Nilai Deal</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-3.5">
                  <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">CPA / CPP</p>
                  <h3 className="text-sm font-black font-mono text-rose-600 mt-1">Rp {cpaCpp.toLocaleString('id-ID')}</h3>
                  <p className="text-[9px] text-slate-500 mt-1">Biaya per Client Closing</p>
                </CardContent>
              </Card>

            </div>

            {/* Quick Funnel & Top Client Preview Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Funnel Preview */}
              <Card className="bg-white border-slate-200/80 shadow-xs p-5 space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                  <h4 className="font-bold text-xs text-slate-900 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" /> Ringkasan Sales Funnel
                  </h4>
                  <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                    Conversion Rate: {dealPercentage}%
                  </span>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex justify-between items-center p-2.5 bg-blue-50/50 rounded-xl border border-blue-100">
                    <span className="font-bold text-slate-700">1. Impression</span>
                    <span className="font-mono font-bold text-slate-900">{totalImpressions.toLocaleString('id-ID')}</span>
                  </div>
                  <div className="flex justify-between items-center p-2.5 bg-blue-50 rounded-xl border border-blue-100">
                    <span className="font-bold text-slate-700">2. Reach</span>
                    <span className="font-mono font-bold text-slate-900">{totalReach.toLocaleString('id-ID')} <span className="text-slate-500 text-[10px]">({reachPercentage}%)</span></span>
                  </div>
                  <div className="flex justify-between items-center p-2.5 bg-indigo-50 rounded-xl border border-indigo-100">
                    <span className="font-bold text-slate-700">3. Link Click</span>
                    <span className="font-mono font-bold text-slate-900">{totalLinkClicks.toLocaleString('id-ID')} <span className="text-slate-500 text-[10px]">({linkClickPercentage}%)</span></span>
                  </div>
                  <div className="flex justify-between items-center p-2.5 bg-purple-50 rounded-xl border border-purple-100">
                    <span className="font-bold text-slate-700">4. Leads</span>
                    <span className="font-mono font-bold text-slate-900">{leadsList.length} <span className="text-slate-500 text-[10px]">({leadPercentage}%)</span></span>
                  </div>
                  <div className="flex justify-between items-center p-2.5 bg-emerald-50 rounded-xl border border-emerald-200">
                    <span className="font-bold text-emerald-900">5. Deal Client</span>
                    <span className="font-mono font-bold text-emerald-800">{dealCount} Client <span className="text-emerald-700 text-[10px]">({dealPercentage}%)</span></span>
                  </div>
                </div>
              </Card>

              {/* Top Clients Preview */}
              <Card className="bg-white border-slate-200/80 shadow-xs p-5 space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                  <h4 className="font-bold text-xs text-slate-900 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-amber-600" /> Ranking Top Client (Revenue)
                  </h4>
                  <button onClick={() => setActiveMenu('top-clients')} className="text-[10px] font-bold text-amber-600 hover:underline">
                    Lihat Semua &rarr;
                  </button>
                </div>

                <div className="space-y-2 text-xs">
                  {topClients.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <p>Belum ada klien deal yang di-input.</p>
                      <p className="text-[10px] mt-1">Ubah status lead ke <span className="font-bold text-emerald-600">Closing</span> & masukkan nilai invoicenya!</p>
                    </div>
                  ) : (
                    topClients.slice(0, 5).map(c => (
                      <div key={c.id} className="flex justify-between items-center p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-800 font-bold text-[10px] flex items-center justify-center">
                            #{c.rank}
                          </span>
                          <div>
                            <p className="font-bold text-slate-900">{c.name}</p>
                            <p className="text-[10px] text-slate-500">{c.package} • PIC: {c.pic}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold text-emerald-600">Rp {c.revenue.toLocaleString('id-ID')}</p>
                          <span className="text-[10px] text-slate-400 font-semibold">{c.contribution}% Kontribusi</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>

            </div>

          </div>
        )}

        {/* ─── VIEW 2: MARKETING FUNNEL VISUAL (WONYARD SLIDE 4 MATCH) ─── */}
        {activeMenu === 'funnel' && (
          <div className="p-6 space-y-6">
            <div className="flex justify-between items-center bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Marketing Funnel</span>
                <h3 className="font-black text-slate-900 text-lg mt-1">Perjalanan Audiens Hingga Deal Client</h3>
                <p className="text-xs text-slate-500">Visualisasi corong konversi dari penayangan iklan Meta Ads hingga menghasilkan deal klien resmi.</p>
              </div>
              <div className="flex gap-4 text-right">
                <div>
                  <p className="text-[10px] font-bold uppercase text-slate-400">Conversion Rate</p>
                  <p className="text-lg font-black text-emerald-600">{dealPercentage}%</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase text-slate-400">Rasio Lead to Deal</p>
                  <p className="text-lg font-black text-indigo-600">{leadToDealRatio}</p>
                </div>
              </div>
            </div>

            {/* Funnel Visual Container */}
            <Card className="bg-white border-slate-200/80 shadow-xs p-6 max-w-4xl mx-auto">
              <div className="space-y-4">
                
                {/* Stage 1: Impression */}
                <div className="flex items-center gap-4">
                  <div className="w-32 text-right">
                    <p className="font-bold text-xs text-slate-900">Impression</p>
                    <p className="text-[10px] text-slate-400">Tayangan Iklan</p>
                  </div>
                  <div className="flex-1 bg-blue-600 text-white p-3.5 rounded-2xl font-mono font-bold text-center text-sm shadow-sm flex justify-between items-center px-6">
                    <span>Tahap 1</span>
                    <span>{totalImpressions.toLocaleString('id-ID')} Impression</span>
                    <span>100%</span>
                  </div>
                </div>

                {/* Stage 2: Reach */}
                <div className="flex items-center gap-4">
                  <div className="w-32 text-right">
                    <p className="font-bold text-xs text-slate-900">Reach</p>
                    <p className="text-[10px] text-slate-400">Audiens Unik</p>
                  </div>
                  <div className="flex-1 bg-blue-500 text-white p-3.5 rounded-2xl font-mono font-bold text-center text-sm shadow-sm flex justify-between items-center px-6 mx-4">
                    <span>Tahap 2</span>
                    <span>{totalReach.toLocaleString('id-ID')} Reach</span>
                    <span>{reachPercentage}% dari Impression</span>
                  </div>
                </div>

                {/* Stage 3: Link Click */}
                <div className="flex items-center gap-4">
                  <div className="w-32 text-right">
                    <p className="font-bold text-xs text-slate-900">Link Click</p>
                    <p className="text-[10px] text-slate-400">Klik Web Satala</p>
                  </div>
                  <div className="flex-1 bg-indigo-500 text-white p-3.5 rounded-2xl font-mono font-bold text-center text-sm shadow-sm flex justify-between items-center px-6 mx-8">
                    <span>Tahap 3</span>
                    <span>{totalLinkClicks.toLocaleString('id-ID')} Clicks</span>
                    <span>{linkClickPercentage}% dari Reach</span>
                  </div>
                </div>

                {/* Stage 4: Leads */}
                <div className="flex items-center gap-4">
                  <div className="w-32 text-right">
                    <p className="font-bold text-xs text-slate-900">Leads</p>
                    <p className="text-[10px] text-slate-400">Inkuiri Chat WA</p>
                  </div>
                  <div className="flex-1 bg-purple-500 text-white p-3.5 rounded-2xl font-mono font-bold text-center text-sm shadow-sm flex justify-between items-center px-6 mx-12">
                    <span>Tahap 4</span>
                    <span>{leadsList.length} Leads</span>
                    <span>{leadPercentage}% dari Clicks</span>
                  </div>
                </div>

                {/* Stage 5: Deal Client */}
                <div className="flex items-center gap-4">
                  <div className="w-32 text-right">
                    <p className="font-bold text-xs text-emerald-700">Deal Client</p>
                    <p className="text-[10px] text-slate-400">Klien Closing</p>
                  </div>
                  <div className="flex-1 bg-emerald-600 text-white p-4 rounded-2xl font-mono font-bold text-center text-sm shadow-md flex justify-between items-center px-6 mx-16">
                    <span>Tahap 5 (Closing)</span>
                    <span>{dealCount} Client Deal</span>
                    <span>{dealPercentage}% dari Leads</span>
                  </div>
                </div>

              </div>
            </Card>
          </div>
        )}

        {/* ─── VIEW 4: RANKING TOP CLIENT (WONYARD SLIDE 7 MATCH) ─── */}
        {activeMenu === 'top-clients' && (
          <div className="p-6 space-y-6">
            <div className="flex justify-between items-center bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Revenue Breakdown</span>
                <h3 className="font-black text-slate-900 text-lg mt-1">Distribusi & Ranking Top Client</h3>
                <p className="text-xs text-slate-500">Daftar klien yang berhasil closing beserta kontribusi revenue terhadap total pencapaian perusahaan.</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold uppercase text-slate-400">Total Closing Revenue</p>
                <p className="text-xl font-black text-emerald-600">Rp {totalClosingRevenue.toLocaleString('id-ID')}</p>
              </div>
            </div>

            <Card className="bg-white border-slate-200/80 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left text-slate-700">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider border-b border-slate-200 font-extrabold">
                    <tr>
                      <th className="py-3.5 px-4">Rangking</th>
                      <th className="py-3.5 px-4">Nama Client</th>
                      <th className="py-3.5 px-4">Paket Maklon</th>
                      <th className="py-3.5 px-4">PIC Admin Sales</th>
                      <th className="py-3.5 px-4">Sumber Lead</th>
                      <th className="py-3.5 px-4">Nilai Invoice Revenue (Rp)</th>
                      <th className="py-3.5 px-4">% Kontribusi Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {topClients.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="py-12 text-center text-slate-400 font-medium">
                          Belum ada klien closing tercatat. Buka menu <span className="font-bold text-blue-600">Leads & Closing CRM</span> dan ubah status lead menjadi <span className="font-bold text-emerald-600">Closing</span> untuk memasukkan invoice!
                        </td>
                      </tr>
                    ) : (
                      topClients.map(c => (
                        <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                          <td className="py-3.5 px-4">
                            <span className={`w-6 h-6 rounded-full font-bold text-xs flex items-center justify-center ${
                              c.rank === 1 ? 'bg-amber-400 text-slate-950 font-black' :
                              c.rank === 2 ? 'bg-slate-300 text-slate-900 font-bold' :
                              c.rank === 3 ? 'bg-amber-700 text-white font-bold' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              #{c.rank}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 font-bold text-slate-900">{c.name}</td>
                          <td className="py-3.5 px-4 text-slate-600">{c.package}</td>
                          <td className="py-3.5 px-4 font-bold text-slate-700">{c.pic}</td>
                          <td className="py-3.5 px-4">
                            <span className="text-[10px] font-bold bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                              {c.source}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 font-mono font-bold text-emerald-600 text-sm">
                            Rp {c.revenue.toLocaleString('id-ID')}
                          </td>
                          <td className="py-3.5 px-4 font-bold text-slate-800">{c.contribution}%</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ─── VIEW 5: LIVE VISITORS & USER ANALYTICS PAGE (100% REALTIME) ─── */}
        {activeMenu === 'visitors' && (
          <div className="p-6 space-y-6">
            <div className="flex justify-between items-center bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-full flex items-center gap-1.5 w-fit">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> Realtime Traffic & User Identification
                </span>
                <h3 className="font-black text-slate-900 text-lg mt-1">Live Visitors & Akun Pengguna Web</h3>
                <p className="text-xs text-slate-500">Pemantauan aktivitas realtime, status online, re-engagement sesi, dan status verifikasi user.</p>
              </div>
              <div className="flex gap-4 text-right">
                <div>
                  <p className="text-[10px] font-bold uppercase text-slate-400">Total Sesi Realtime</p>
                  <p className="text-xl font-black text-slate-900">{realtimeVisitors.length} Sesi</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase text-slate-400">Live Online Sekarang</p>
                  <p className="text-xl font-black text-emerald-600">{realtimeVisitors.length} Online</p>
                </div>
              </div>
            </div>

            {/* 4 Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-4">
                  <p className="text-[10px] font-bold uppercase text-slate-400">Online Realtime</p>
                  <h3 className="text-2xl font-black text-emerald-600 mt-1 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-emerald-500 animate-ping"></span> {realtimeVisitors.length} Orang
                  </h3>
                  <p className="text-[10px] text-slate-500 mt-1">Sedang Buka Web Detik Ini</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-4">
                  <p className="text-[10px] font-bold uppercase text-slate-400">Tim Internal (Backend)</p>
                  <h3 className="text-2xl font-black text-purple-600 mt-1">
                    {realtimeVisitors.filter(v => v.category === 'INTERNAL_ADMIN').length} Admin
                  </h3>
                  <p className="text-[10px] text-slate-500 mt-1">Portal Digital & Office</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-4">
                  <p className="text-[10px] font-bold uppercase text-slate-400">Client Terverifikasi</p>
                  <h3 className="text-2xl font-black text-blue-600 mt-1">
                    {realtimeVisitors.filter(v => v.category === 'CLIENT_VERIFIED').length} Client Login
                  </h3>
                  <p className="text-[10px] text-slate-500 mt-1">Akun Login di Public Web</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-4">
                  <p className="text-[10px] font-bold uppercase text-slate-400">Visitor Public (Iklan)</p>
                  <h3 className="text-2xl font-black text-amber-600 mt-1">
                    {realtimeVisitors.filter(v => v.category === 'VISITOR_ANONYMOUS').length} Visitor
                  </h3>
                  <p className="text-[10px] text-slate-500 mt-1">Belum Login (Landing Page)</p>
                </CardContent>
              </Card>
            </div>

            {/* Architectural Explanation Card */}
            <Card className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white p-5 rounded-2xl shadow-sm">
              <div className="space-y-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded border border-amber-400/20">Klasifikasi User Sesuai Akses</span>
                <h4 className="font-bold text-sm text-white">Klasifikasi Pengguna di Web Satala Dermatech</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-slate-300 mt-2">
                  <div className="bg-white/10 p-3 rounded-xl -xs">
                    <p className="font-bold text-purple-300 mb-1">1. 🔑 Tim Internal / Backend</p>
                    <p>Tim Satala (Marketing & Office) yang sedang mengontrol sistem internal di portal backend <code>/digitalmarketing</code> atau <code>/office</code>.</p>
                  </div>
                  <div className="bg-white/10 p-3 rounded-xl -xs">
                    <p className="font-bold text-blue-300 mb-1">2. 🟢 Client Terverifikasi</p>
                    <p>Klien resmi Satala yang sudah memiliki akun dan login di halaman public landing page tanpa masuk ke sistem backend internal.</p>
                  </div>
                  <div className="bg-white/10 p-3 rounded-xl -xs">
                    <p className="font-bold text-amber-300 mb-1">3. 🌐 Visitor Public / Ads</p>
                    <p>Pengunjung umum dari Meta Ads, Google Ads, TikTok Ads, atau browsing biasa di landing page depan yang belum login.</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Live Visitors Stream Table with Separated Filter Tabs */}
            <Card className="bg-white border-slate-200/80 shadow-xs overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-emerald-600" />
                  <h4 className="font-bold text-xs text-slate-900">Activity Stream Realtime Visitors</h4>
                </div>

                {/* Separated Filter Tabs */}
                <div className="flex flex-wrap bg-slate-100 p-1 rounded-xl gap-1 text-xs font-bold">
                  <button
                    onClick={() => setVisitorTabFilter('ALL')}
                    className={`px-3 py-1.5 rounded-lg transition-all ${
                      visitorTabFilter === 'ALL'
                        ? 'bg-white text-slate-900 shadow-xs font-black'
                        : 'text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    Semua ({realtimeVisitors.length})
                  </button>
                  <button
                    onClick={() => setVisitorTabFilter('INTERNAL')}
                    className={`px-3 py-1.5 rounded-lg transition-all ${
                      visitorTabFilter === 'INTERNAL'
                        ? 'bg-purple-600 text-white shadow-xs font-black'
                        : 'text-slate-500 hover:text-purple-700'
                    }`}
                  >
                    🔑 Backend Internal ({realtimeVisitors.filter(v => v.category === 'INTERNAL_ADMIN').length})
                  </button>
                  <button
                    onClick={() => setVisitorTabFilter('CLIENT')}
                    className={`px-3 py-1.5 rounded-lg transition-all ${
                      visitorTabFilter === 'CLIENT'
                        ? 'bg-blue-600 text-white shadow-xs font-black'
                        : 'text-slate-500 hover:text-blue-700'
                    }`}
                  >
                    🟢 Client Terverifikasi ({realtimeVisitors.filter(v => v.category === 'CLIENT_VERIFIED').length})
                  </button>
                  <button
                    onClick={() => setVisitorTabFilter('PUBLIC')}
                    className={`px-3 py-1.5 rounded-lg transition-all ${
                      visitorTabFilter === 'PUBLIC'
                        ? 'bg-amber-600 text-white shadow-xs font-black'
                        : 'text-slate-500 hover:text-amber-800'
                    }`}
                  >
                    🌐 Visitor Public ({realtimeVisitors.filter(v => v.category === 'VISITOR_ANONYMOUS').length})
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left text-slate-700">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider border-b border-slate-200 font-extrabold">
                    <tr>
                      <th className="py-3.5 px-4">Pengunjung / User</th>
                      <th className="py-3.5 px-4">Status Identitas Akses</th>
                      <th className="py-3.5 px-4">Perangkat & Browser</th>
                      <th className="py-3.5 px-4">Lokasi & Alamat IP</th>
                      <th className="py-3.5 px-4">Halaman Terakhir Dibuka</th>
                      <th className="py-3.5 px-4">Status Sesi Realtime</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {realtimeVisitors.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="py-12 text-center text-slate-400 font-medium">
                          Belum ada visitor online saat ini. Buka halaman website Satala di tab baru untuk melihat visitor realtime muncul otomatis!
                        </td>
                      </tr>
                    ) : (
                      realtimeVisitors
                        .filter(v => {
                          if (visitorTabFilter === 'INTERNAL') return v.category === 'INTERNAL_ADMIN';
                          if (visitorTabFilter === 'CLIENT') return v.category === 'CLIENT_VERIFIED';
                          if (visitorTabFilter === 'PUBLIC') return v.category === 'VISITOR_ANONYMOUS';
                          return true;
                        })
                        .map(v => (
                          <tr key={v.id} className="hover:bg-slate-50">
                            <td className="py-3.5 px-4 font-bold text-slate-900">
                              {v.name}
                              {v.email !== '-' && <p className="text-[10px] text-slate-400 font-mono font-normal">{v.email}</p>}
                            </td>
                            <td className="py-3.5 px-4">
                              <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full shadow-xs ${
                                v.category === 'INTERNAL_ADMIN'
                                  ? 'bg-purple-100 text-purple-800 border border-purple-200'
                                  : v.category === 'CLIENT_VERIFIED'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-amber-100 text-amber-800 border border-amber-200'
                              }`}>
                                {v.category === 'INTERNAL_ADMIN'
                                  ? '🔑 Backend Admin / Tim Satala'
                                  : v.category === 'CLIENT_VERIFIED'
                                  ? '🟢 Client Terverifikasi (Public Web)'
                                  : `🌐 Visitor Public (${v.source})`}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 text-slate-600">{v.device}</td>
                            <td className="py-3.5 px-4 font-semibold text-slate-800">
                              {v.city}
                              <span className="text-[10px] text-slate-500 font-mono block font-normal">IP: {v.ip || '127.0.0.1 (Localhost)'}</span>
                            </td>
                            <td className="py-3.5 px-4 font-mono text-blue-600 font-bold">{v.page}</td>
                            <td className="py-3.5 px-4">
                              <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1 w-fit">
                                <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full animate-ping"></span> Online Sekarang
                              </span>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ─── VIEW 3: LEADS PAGE ─── */}
        {activeMenu === 'leads' && (
          <div className="p-6 space-y-4">
            
            {/* Top Toolbar */}
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 bg-white p-4 rounded-2xl border border-slate-200/80">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cari nama client, email, atau phone..."
                  value={leadsSearch}
                  onChange={e => setLeadsSearch(e.target.value)}
                  className="pl-9 h-10 bg-slate-50 border-slate-200 text-xs rounded-xl"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Select value={leadSourceFilter} onValueChange={setLeadSourceFilter}>
                  <SelectTrigger className="h-10 text-xs bg-slate-50 border-slate-200 text-slate-800 rounded-xl w-[170px]">
                    <SelectValue placeholder="Sumber Traffic" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-800">
                    <SelectItem value="ALL">Semua Traffic</SelectItem>
                    <SelectItem value="Meta Ads">Meta Ads (FB/IG)</SelectItem>
                    <SelectItem value="Google Ads">Google Ads</SelectItem>
                    <SelectItem value="TikTok Ads">TikTok Ads</SelectItem>
                    <SelectItem value="Non-Iklan (Organik)">Non-Iklan (Pure Web)</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={leadStatusFilter} onValueChange={setLeadStatusFilter}>
                  <SelectTrigger className="h-10 text-xs bg-slate-50 border-slate-200 text-slate-800 rounded-xl w-[150px]">
                    <SelectValue placeholder="Status Filter" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-800">
                    <SelectItem value="ALL">Semua Status</SelectItem>
                    <SelectItem value="Baru">Baru</SelectItem>
                    <SelectItem value="Belum Follow Up">Belum Follow Up</SelectItem>
                    <SelectItem value="Sudah Dihubungi">Sudah Dihubungi</SelectItem>
                    <SelectItem value="Follow Up">Follow Up</SelectItem>
                    <SelectItem value="Negosiasi">Negosiasi</SelectItem>
                    <SelectItem value="Closing">Closing</SelectItem>
                  </SelectContent>
                </Select>

                <Button 
                  onClick={handleResetAllData}
                  variant="outline"
                  className="border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-bold h-10 rounded-xl gap-2 shadow-xs"
                >
                  <RotateCcw className="w-4 h-4" /> Reset Data Leads
                </Button>

                <Button 
                  onClick={() => setIsAddLeadOpen(true)}
                  className="bg-[#8C6208] hover:bg-[#724F06] text-white text-xs font-bold h-10 rounded-xl gap-2 shadow-sm"
                >
                  <Plus className="w-4 h-4" /> Tambah Lead
                </Button>
              </div>
            </div>

            {/* Leads Table */}
            <Card className="bg-white border-slate-200/80 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left text-slate-700">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider border-b border-slate-200 font-extrabold">
                    <tr>
                      <th className="py-3.5 px-4">Nama / Perusahaan</th>
                      <th className="py-3.5 px-4">Sumber Traffic</th>
                      <th className="py-3.5 px-4">Tanggal</th>
                      <th className="py-3.5 px-4">Status</th>
                      <th className="py-3.5 px-4">PIC</th>
                      <th className="py-3.5 px-4 text-center">Aksi Chat</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredLeads.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="py-12 text-center text-slate-400 font-medium">
                          Belum ada data lead. Klik tombol <span className="font-bold text-[#8C6208]">+ Tambah Lead</span> di atas untuk menambahkan lead baru.
                        </td>
                      </tr>
                    ) : (
                      filteredLeads.map(lead => (
                      <tr 
                        key={lead.id} 
                        className="hover:bg-slate-50/80 cursor-pointer transition-colors"
                        onClick={() => openLeadDetail(lead)}
                      >
                        <td className="py-3.5 px-4">
                          <p className="font-bold text-slate-900 text-xs">{lead.name}</p>
                          <p className="text-[10px] text-slate-400">{lead.email}</p>
                        </td>
                        <td className="py-3.5 px-4">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                            lead.source === 'Google Ads' ? 'bg-blue-50 text-blue-700' :
                            lead.source === 'Meta Ads' ? 'bg-rose-50 text-rose-700' :
                            lead.source === 'TikTok Ads' ? 'bg-purple-50 text-purple-700' :
                            'bg-emerald-50 text-emerald-700'
                          }`}>
                            {lead.source}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-[11px] font-mono text-slate-500">{lead.date}</td>
                        <td className="py-3.5 px-4">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                            lead.status === 'Baru' ? 'bg-blue-100 text-blue-700' :
                            lead.status === 'Belum Follow Up' ? 'bg-rose-100 text-rose-700' :
                            lead.status === 'Sudah Dihubungi' ? 'bg-emerald-100 text-emerald-700' :
                            lead.status === 'Follow Up' ? 'bg-amber-100 text-amber-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {lead.status}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 font-semibold text-slate-800">{lead.pic}</td>
                        <td className="py-3.5 px-4 text-center">
                          <a
                            href={`https://wa.me/${(lead.phone || '').replace(/[^0-9]/g, '')}?text=Halo%20${encodeURIComponent(lead.name || '')},%20terima%20kasih%20telah%20menghubungi%20Satala%20Dermatech.`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[10px] rounded-lg transition-all shadow-xs"
                          >
                            <MessageSquare className="w-3 h-3" /> Chat WA
                          </a>
                        </td>
                      </tr>
                    ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

          </div>
        )}

        {/* ─── VIEW 3: LEAD DETAIL PAGE ─── */}
        {activeMenu === 'lead-detail' && selectedLead && (
          <div className="p-6 space-y-6">
            
            {/* Header Detail */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-black text-slate-900">{selectedLead.name}</h3>
                  <span className="text-xs bg-blue-100 text-blue-700 font-bold px-2.5 py-0.5 rounded-full">{selectedLead.status}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">Sumber: <span className="font-bold text-slate-800">{selectedLead.source}</span> • Tanggal: {selectedLead.date} • PIC: <span className="font-bold text-slate-800">{selectedLead.pic}</span></p>
              </div>

              <div className="flex gap-2">
                <Select value={selectedLead.status} onValueChange={(val) => handleUpdateLeadStatus(selectedLead.id, val)}>
                  <SelectTrigger className="h-10 text-xs bg-amber-50 border-amber-200 text-[#8C6208] font-bold rounded-xl w-[160px]">
                    <SelectValue placeholder="Ubah Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-800">
                    <SelectItem value="Baru">Baru</SelectItem>
                    <SelectItem value="Belum Follow Up">Belum Follow Up</SelectItem>
                    <SelectItem value="Sudah Dihubungi">Sudah Dihubungi</SelectItem>
                    <SelectItem value="Follow Up">Follow Up</SelectItem>
                    <SelectItem value="Negosiasi">Negosiasi</SelectItem>
                    <SelectItem value="Closing">Closing</SelectItem>
                    <SelectItem value="Tidak Jadi">Tidak Jadi</SelectItem>
                  </SelectContent>
                </Select>

                <Button onClick={() => setActiveMenu('leads')} variant="outline" className="text-xs font-bold rounded-xl h-10">
                  Kembali ke Leads
                </Button>
              </div>
            </div>

            {/* Grid 2 Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Column 1: Contact Info */}
              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-5 space-y-4">
                  <h4 className="font-bold text-xs text-slate-900 border-b border-slate-100 pb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#8C6208]" /> Informasi Kontak & Perusahaan
                  </h4>

                  <div className="space-y-3 text-xs">
                    <div className="flex justify-between"><span className="text-slate-400">Email</span><span className="font-bold text-slate-800">{selectedLead.email}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">No. WhatsApp</span><span className="font-mono font-bold text-slate-800">{selectedLead.phone}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Kota / Lokasi</span><span className="font-bold text-slate-800">{selectedLead.city}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">UTM Tracking Link</span><span className="font-mono text-[10px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{selectedLead.utm}</span></div>
                  </div>
                </CardContent>
              </Card>

              {/* Column 2: Kebutuhan Maklon */}
              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-5 space-y-4">
                  <h4 className="font-bold text-xs text-slate-900 border-b border-slate-100 pb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#8C6208]" /> Kebutuhan Maklon
                  </h4>

                  <div className="space-y-3 text-xs">
                    <div className="flex justify-between"><span className="text-slate-400">Paket Diminati</span><span className="font-bold text-[#8C6208]">{selectedLead.package}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Target MOQ</span><span className="font-bold text-slate-800">{selectedLead.moq}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Estimasi Order</span><span className="font-bold text-slate-800">{selectedLead.estOrder}</span></div>
                  </div>

                  <a
                    href={`https://wa.me/${(selectedLead?.phone || '').replace(/[^0-9]/g, '')}?text=Halo%20${encodeURIComponent(selectedLead?.name || '')},%20terima%20kasih%20telah%20menghubungi%20Satala%20Dermatech.`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full mt-4 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 rounded-xl transition-all shadow-sm"
                  >
                    <MessageSquare className="w-4 h-4" /> Hubungi via WhatsApp Now
                  </a>
                </CardContent>
              </Card>

            </div>

          </div>
        )}

        {/* ─── VIEW 4: ANALYTICS PAGE ─── */}
        {activeMenu === 'analytics' && (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-4">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Visitors</p>
                  <h3 className="text-2xl font-black text-slate-900 mt-1">{INITIAL_VISITORS.length}</h3>
                  <span className="text-[10px] text-emerald-600 font-semibold">Realtime Analytics</span>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-4">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Leads</p>
                  <h3 className="text-2xl font-black text-slate-900 mt-1">{leadsList.length}</h3>
                  <span className="text-[10px] text-emerald-600 font-semibold">Realtime Prospek</span>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-4">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Conversion Rate</p>
                  <h3 className="text-2xl font-black text-slate-900 mt-1">
                    {leadsList.length > 0 ? ((leadsList.filter(l => l.status === 'Closing').length / leadsList.length) * 100).toFixed(1) + '%' : '0.0%'}
                  </h3>
                  <span className="text-[10px] text-emerald-600 font-semibold">Terkalkulasi Otomatis</span>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-4">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Cost per Lead</p>
                  <h3 className="text-2xl font-black text-slate-900 mt-1">
                    {leadsList.length > 0 ? `Rp ${(50000 / leadsList.length).toLocaleString('id-ID')}` : 'Rp 0'}
                  </h3>
                  <span className="text-[10px] text-emerald-600 font-semibold">Berdasarkan Spend Ads</span>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ─── VIEW 5: CAMPAIGNS PAGE ─── */}
        {activeMenu === 'campaigns' && (
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-center bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-slate-900 text-sm">Kelola Campaign Iklan & Biaya Spent</h3>
                  <span className="text-[10px] font-mono bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-bold border border-blue-200">
                    Ad Account: {pixelConfig.metaAdAccountId || '779514711733398'}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">Catat anggaran harian Meta/Google Ads dan spent aktual untuk menghitung ROAS & CPL presisi.</p>
              </div>

              <div className="flex items-center gap-2">
                <Button 
                  onClick={handleSyncMetaAds}
                  disabled={isSyncingMeta}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold h-9 rounded-xl gap-1.5 shadow-sm"
                >
                  <Zap className="w-3.5 h-3.5 fill-white text-white" /> 
                  {isSyncingMeta ? 'Mengambil Data Meta...' : 'Sync Realtime Meta Ads'}
                </Button>

                <Button 
                  onClick={() => setIsAddCampaignOpen(true)}
                  className="bg-[#8C6208] hover:bg-[#724F06] text-white text-xs font-bold h-9 rounded-xl gap-1.5 shadow-sm"
                >
                  <Plus className="w-4 h-4" /> Tambah Manual
                </Button>
              </div>
            </div>

            <Card className="bg-white border-slate-200/80 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left text-slate-700">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider border-b border-slate-200 font-extrabold">
                    <tr>
                      <th className="py-3.5 px-3">Nama Kampanye / Set Iklan</th>
                      <th className="py-3.5 px-3">Penayangan</th>
                      <th className="py-3.5 px-3">Jangkauan</th>
                      <th className="py-3.5 px-3">Impresi</th>
                      <th className="py-3.5 px-3">Frekuensi</th>
                      <th className="py-3.5 px-3">Hasil</th>
                      <th className="py-3.5 px-3">Spent Terpakai</th>
                      <th className="py-3.5 px-3">Biaya / Hasil (CPR)</th>
                      <th className="py-3.5 px-3">CPC (Tautan)</th>
                      <th className="py-3.5 px-3">CPM (1rb Tayangan)</th>
                      <th className="py-3.5 px-3">CTR (Semua)</th>
                      <th className="py-3.5 px-3">ROAS</th>
                      <th className="py-3.5 px-3 text-center">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {campaignsList.length === 0 ? (
                      <tr>
                        <td colSpan="13" className="py-12 text-center text-slate-400 font-medium">
                          Belum ada campaign iklan tercatat. Klik tombol <span className="font-bold text-blue-600">⚡ Sync Realtime Meta Ads</span> di atas untuk menarik data iklan Meta Anda.
                        </td>
                      </tr>
                    ) : (
                      campaignsList.map(c => {
                        const campaignROAS = (c.spent && Number(c.spent) > 0)
                          ? (totalClosingRevenue / Number(c.spent)).toFixed(2)
                          : '0.00';

                        return (
                          <tr key={c.id} className="hover:bg-slate-50/90 transition-colors text-[11px]">
                            <td className="py-3.5 px-3">
                              <p className="font-bold text-blue-600 hover:underline cursor-pointer">{c.name}</p>
                              <p className="text-[10px] text-slate-400">{c.setIklan || 'Set Iklan Meta'}</p>
                            </td>
                            <td className="py-3.5 px-3">
                              <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                c.status === 'Aktif' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
                              }`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> {c.status}
                              </span>
                            </td>
                            <td className="py-3.5 px-3 font-mono">{(c.reach || 0).toLocaleString('id-ID')}</td>
                            <td className="py-3.5 px-3 font-mono">{(c.impressions || 0).toLocaleString('id-ID')}</td>
                            <td className="py-3.5 px-3 font-mono text-slate-600">{c.frequency || '1.00'}</td>
                            <td className="py-3.5 px-3 font-bold text-slate-800">{c.results || 0}</td>
                            <td className="py-3.5 px-3 font-mono font-bold text-slate-900">
                              Rp {(Number(c.spent) || 0).toLocaleString('id-ID')}
                            </td>
                            <td className="py-3.5 px-3 font-mono text-slate-700">
                              {c.cpr > 0 ? `Rp ${(c.cpr).toLocaleString('id-ID')}` : '-'}
                            </td>
                            <td className="py-3.5 px-3 font-mono text-slate-700">
                              {c.cpc > 0 ? `Rp ${(c.cpc).toLocaleString('id-ID')}` : '-'}
                            </td>
                            <td className="py-3.5 px-3 font-mono text-slate-700">
                              {c.cpm > 0 ? `Rp ${(c.cpm).toLocaleString('id-ID')}` : '-'}
                            </td>
                            <td className="py-3.5 px-3 font-bold text-slate-800">{c.ctr || '0.00%'}</td>
                            <td className="py-3.5 px-3 font-black text-amber-600">{campaignROAS}x</td>
                            <td className="py-3.5 px-3 text-center">
                              <button
                                onClick={() => handleDeleteCampaign(c.id)}
                                className="text-rose-500 hover:text-rose-700 p-1 hover:bg-rose-50 rounded-md transition-colors"
                                title="Hapus Campaign"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ─── VIEW 6: LANDING PAGES VIEW (NEW) ─── */}
        {activeMenu === 'landing-pages' && (
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-center bg-white p-4 rounded-2xl border border-slate-200/80">
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Kelola Landing Page Active</h3>
                <p className="text-xs text-slate-500">Monitor views, lead conversion rate, & status publikasi halaman website.</p>
              </div>
              <Button onClick={() => window.open('/pricing/parfum', '_blank')} className="bg-[#8C6208] hover:bg-[#724F06] text-white text-xs font-bold h-9 rounded-xl gap-1.5">
                <ExternalLink className="w-3.5 h-3.5" /> Buka Live Website
              </Button>
            </div>

            <Card className="bg-white border-slate-200/80 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left text-slate-700">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider border-b border-slate-200 font-extrabold">
                    <tr>
                      <th className="py-3.5 px-4">Judul Landing Page</th>
                      <th className="py-3.5 px-4">URL Path</th>
                      <th className="py-3.5 px-4">Status</th>
                      <th className="py-3.5 px-4">Visitors</th>
                      <th className="py-3.5 px-4">Leads</th>
                      <th className="py-3.5 px-4">Conversion Rate</th>
                      <th className="py-3.5 px-4 text-center">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {landingPages.map(lp => (
                      <tr key={lp.id} className="hover:bg-slate-50">
                        <td className="py-3.5 px-4 font-bold text-slate-900">{lp.title}</td>
                        <td className="py-3.5 px-4 font-mono text-[11px] text-blue-600">{lp.path}</td>
                        <td className="py-3.5 px-4">
                          <button 
                            onClick={() => handleToggleLandingPage(lp.id)}
                            className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full transition-colors ${
                              lp.status === 'Aktif' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {lp.status}
                          </button>
                        </td>
                        <td className="py-3.5 px-4 font-mono">{lp.views.toLocaleString('id-ID')}</td>
                        <td className="py-3.5 px-4 font-bold text-slate-900">{lp.leads}</td>
                        <td className="py-3.5 px-4 font-black text-amber-600">{lp.conv}</td>
                        <td className="py-3.5 px-4 text-center">
                          <div className="flex justify-center gap-2">
                            <button onClick={() => window.open(lp.path, '_blank')} className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg" title="Preview">
                              <ExternalLink className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleCopyText(window.location.origin + lp.path)} className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg" title="Copy Link">
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ─── VIEW 7: KELOLA ADMIN WHATSAPP & ROTATOR (NEW) ─── */}
        {activeMenu === 'whatsapp' && (
          <div className="p-6 space-y-6">
            {/* Header & Rotator Explanation */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs">
              <div>
                <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-emerald-600" />
                  Kelola Rotasi Admin WhatsApp Sales / CS
                </h3>
                <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                  Sistem rotator otomatis membagikan giliran chat WA dari pengunjung website secara adil (Round-Robin) kepada admin berstatus 
                  <span className="font-bold text-emerald-600"> AKTIF</span>. Jika dinonaktifkan, chat 100% dialihkan ke admin yang aktif.
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button 
                  onClick={handleResetAllData}
                  variant="outline"
                  className="border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-bold h-10 rounded-xl gap-2 shadow-xs"
                >
                  <RotateCcw className="w-4 h-4" /> Reset Hitungan Chat & Leads
                </Button>

                <Button 
                  onClick={() => setIsAddAdminOpen(true)}
                  className="bg-[#8C6208] hover:bg-[#724F06] text-white text-xs font-bold h-10 rounded-xl gap-2 shadow-xs"
                >
                  <UserPlus className="w-4 h-4" /> + Tambah Admin WA
                </Button>
              </div>
            </div>

            {/* Admin Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {waAdmins.length === 0 ? (
                <div className="col-span-full bg-white p-8 rounded-2xl border border-slate-200/80 text-center space-y-3 shadow-xs">
                  <MessageSquare className="w-10 h-10 text-slate-300 mx-auto" />
                  <p className="font-bold text-slate-800 text-sm">Belum Ada Admin WhatsApp Terdaftar</p>
                  <p className="text-xs text-slate-500 max-w-md mx-auto">
                    Klik tombol <span className="font-bold text-[#8C6208]">+ Tambah Admin WA</span> di kanan atas untuk mendaftarkan nomor WhatsApp sales/CS pertama Anda.
                  </p>
                </div>
              ) : (
                waAdmins.map(admin => (
                <Card 
                  key={admin.id} 
                  className={`bg-white border-2 transition-all shadow-xs overflow-hidden ${
                    admin.active ? 'border-emerald-500/40 ring-1 ring-emerald-500/20' : 'border-slate-200 opacity-75'
                  }`}
                >
                  <CardContent className="p-5 space-y-4">
                    {/* Top Row: Avatar & Toggle */}
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center font-extrabold text-sm ${
                          admin.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-400'
                        }`}>
                          {admin.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <h4 className="font-extrabold text-slate-900 text-sm">{admin.name}</h4>
                          <p className="text-[11px] font-semibold text-slate-500">{admin.role}</p>
                        </div>
                      </div>

                      {/* Active/Inactive Switch Toggle */}
                      <button
                        onClick={() => handleToggleAdminActive(admin.id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold transition-all cursor-pointer shadow-xs ${
                          admin.active 
                            ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                            : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                        }`}
                        title="Klik untuk mengubah status aktif"
                      >
                        {admin.active ? (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5" /> AKTIF
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-3.5 h-3.5" /> NONAKTIF
                          </>
                        )}
                      </button>
                    </div>

                    {/* Phone & Statistics */}
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 text-[11px]">No. WhatsApp:</span>
                        <span className="font-mono font-bold text-slate-800">{admin.phone}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 text-[11px]">Total Chat Diterima:</span>
                        <span className="font-extrabold text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                          {admin.leadsCount || 0} Chat Masuk
                        </span>
                      </div>
                    </div>

                    {/* Action Bar */}
                    <div className="flex justify-between items-center pt-1 border-t border-slate-100 text-xs">
                      <a
                        href={`https://wa.me/${(admin?.phone || '').replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-600 hover:text-emerald-700 font-bold text-[11px] inline-flex items-center gap-1"
                      >
                        <MessageSquare className="w-3.5 h-3.5" /> Tes Direct Chat
                      </a>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingAdmin(admin)}
                          className="text-amber-600 hover:text-amber-700 p-1.5 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Edit Admin WA"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAdmin(admin.id)}
                          className="text-rose-500 hover:text-rose-700 p-1.5 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Hapus Admin WA"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
              )}
            </div>

            {/* Quick Reply & Template Library Section */}
            <div className="pt-6 border-t border-slate-200">
              <h3 className="font-bold text-slate-900 text-sm mb-1">WhatsApp Quick Reply & Template Library</h3>
              <p className="text-xs text-slate-500 mb-4">Salin cepat pesan standar follow up untuk memudahkan respon tim sales & CS.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {WA_TEMPLATES.map(tpl => (
                  <Card key={tpl.id} className="bg-white border-slate-200/80 shadow-xs hover:border-amber-300 transition-colors">
                    <CardContent className="p-5 space-y-3">
                      <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                        <span className="font-bold text-xs text-slate-900">{tpl.name}</span>
                        <span className="text-[10px] font-bold bg-amber-50 text-[#8C6208] px-2 py-0.5 rounded">{tpl.category}</span>
                      </div>

                      <p className="text-xs text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100 leading-relaxed font-sans">
                        {tpl.text}
                      </p>

                      <div className="flex justify-end gap-2 pt-1">
                        <Button onClick={() => handleCopyText(tpl.text)} variant="outline" className="text-xs h-8 gap-1.5 font-semibold">
                          <Copy className="w-3.5 h-3.5" /> Salin Pesan
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─── VIEW 8: VISITORS LIVE PAGE ─── */}
        {activeMenu === 'visitors' && (
          <div className="p-6 space-y-4">
            <Card className="bg-white border-slate-200/80 shadow-xs p-5">
              <h4 className="font-bold text-sm text-slate-900 mb-4">Pengunjung Live (Realtime 18 Online)</h4>
              <div className="space-y-3 text-xs">
                {INITIAL_VISITORS.map(v => (
                  <div key={v.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <div>
                      <p className="font-bold text-slate-900">{v.city}</p>
                      <p className="text-slate-500 text-[11px]">{v.page}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-blue-600 font-bold block">{v.source}</span>
                      <span className="text-[10px] text-slate-400">{v.duration} • {v.device}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ─── VIEW 9: FORMS PAGE ─── */}
        {activeMenu === 'forms' && (
          <div className="p-6 space-y-4">
            <Card className="bg-white border-slate-200/80 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left text-slate-700">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider border-b border-slate-200 font-extrabold">
                    <tr>
                      <th className="py-3.5 px-4">Nama Form Lead</th>
                      <th className="py-3.5 px-4">Email</th>
                      <th className="py-3.5 px-4">No. WhatsApp</th>
                      <th className="py-3.5 px-4">Tanggal Masuk</th>
                      <th className="py-3.5 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {INITIAL_FORMS.map(f => (
                      <tr key={f.id} className="hover:bg-slate-50">
                        <td className="py-3.5 px-4 font-bold text-slate-900">{f.name}</td>
                        <td className="py-3.5 px-4 text-slate-500">{f.email}</td>
                        <td className="py-3.5 px-4 font-mono">{f.phone}</td>
                        <td className="py-3.5 px-4 text-slate-500">{f.date}</td>
                        <td className="py-3.5 px-4">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${f.status === 'Baru' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>{f.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ─── VIEW 10: REPORTS PAGE (NEW) ─── */}
        {activeMenu === 'reports' && (
          <div className="p-6 space-y-6">
            <div className="flex justify-between items-center bg-white p-4 rounded-2xl border border-slate-200/80">
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Laporan Kinerja Digital Marketing</h3>
                <p className="text-xs text-slate-500">Ringkasan ROI, Cost per Lead (CPL), & perolehan lead bulanan.</p>
              </div>
              <Button onClick={() => toast.success('Laporan berhasil diunduh dalam format PDF/Excel!')} className="bg-[#8C6208] hover:bg-[#724F06] text-white text-xs font-bold h-9 rounded-xl gap-1.5">
                <Download className="w-3.5 h-3.5" /> Export Laporan
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-5 space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Total Budget Marketing (Meta Ads)</p>
                  <h3 className="text-2xl font-black text-slate-900">Rp 50.000 / Hari</h3>
                  <p className="text-xs text-slate-500">Anggaran Harian: <span className="font-mono font-bold text-slate-800">CBO Active</span></p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-5 space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Rata-rata Cost Per Lead (CPL)</p>
                  <h3 className="text-2xl font-black text-emerald-600">
                    {leadsList.length > 0 ? `Rp ${(50000 / leadsList.length).toLocaleString('id-ID')}` : 'Rp 0'}
                  </h3>
                  <p className="text-xs text-emerald-600 font-semibold">Terkalkulasi Realtime</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200/80 shadow-xs">
                <CardContent className="p-5 space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Perkiraan Return On Ad Spend (ROAS)</p>
                  <h3 className="text-2xl font-black text-amber-600">
                    {leadsList.length > 0 ? '1.0x' : '0.0x'}
                  </h3>
                  <p className="text-xs text-slate-500">Estimasi Prospek: <span className="font-bold text-slate-800">{leadsList.length > 0 ? `Rp ${(leadsList.length * 15).toFixed(1)}M` : 'Rp 0'}</span></p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ─── VIEW 11: TASKS PAGE (NEW) ─── */}
        {activeMenu === 'tasks' && (
          <div className="p-6 space-y-6">
            
            {/* Add Task Form */}
            <Card className="bg-white border-slate-200/80 shadow-xs p-4">
              <form onSubmit={handleAddTask} className="flex flex-col sm:flex-row gap-3">
                <Input
                  placeholder="Tulis tugas baru tim marketing..."
                  value={newTaskTitle}
                  onChange={e => setNewTaskTitle(e.target.value)}
                  className="flex-1 text-xs h-10 rounded-xl"
                />
                <Select value={newTaskPriority} onValueChange={setNewTaskPriority}>
                  <SelectTrigger className="w-[130px] text-xs h-10 rounded-xl">
                    <SelectValue placeholder="Prioritas" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Normal">Normal</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="submit" className="bg-[#8C6208] hover:bg-[#724F06] text-white text-xs font-bold h-10 px-5 rounded-xl gap-1.5">
                  <Plus className="w-4 h-4" /> Tambah Tugas
                </Button>
              </form>
            </Card>

            {/* Task List */}
            <Card className="bg-white border-slate-200/80 shadow-xs overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                <h4 className="font-bold text-xs text-slate-900">Daftar Tugas Marketing ({tasks.length})</h4>
                <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                  {tasks.filter(t => t.completed).length} Selesai
                </span>
              </div>

              <div className="divide-y divide-slate-100">
                {tasks.map(t => (
                  <div key={t.id} className="p-4 flex items-center justify-between hover:bg-slate-50/80 transition-colors">
                    <div className="flex items-center gap-3">
                      <input 
                        type="checkbox"
                        checked={t.completed}
                        onChange={() => handleToggleTask(t.id)}
                        className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                      />
                      <span className={`text-xs ${t.completed ? 'line-through text-slate-400' : 'font-semibold text-slate-800'}`}>
                        {t.title}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 text-[10px]">
                      <span className={`font-bold px-2 py-0.5 rounded ${
                        t.priority === 'High' ? 'bg-rose-100 text-rose-700' :
                        t.priority === 'Medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {t.priority}
                      </span>
                      <span className="text-slate-400 font-mono">{t.due}</span>
                      <span className="font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded">{t.pic}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

          </div>
        )}

        {/* ─── VIEW 12: SETTINGS PAGE ─── */}
        {activeMenu === 'settings' && (
          <div className="p-6 max-w-2xl space-y-6">
            <Card className="bg-white border-slate-200/80 shadow-xs">
              <CardContent className="p-6 space-y-4">
                <h4 className="font-bold text-sm text-slate-900 border-b border-slate-100 pb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-[#8C6208]" /> Pengaturan Profil Perusahaan & Pixel
                </h4>

                <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <img src="/logo.png" alt="Satala Logo" className="h-12 object-contain" />
                  <div>
                    <p className="font-bold text-slate-900 text-xs">Satala Dermatech Essential</p>
                    <p className="text-[10px] text-slate-500">Logo Resmi Perusahaan & Branding Digital</p>
                  </div>
                </div>

                <form onSubmit={e => { e.preventDefault(); savePixelConfig(pixelConfig); toast.success('Pengaturan Pixel berhasil disimpan & diaktifkan!'); }} className="space-y-4 text-xs">
                  <div className="space-y-1.5">
                    <Label className="text-slate-700 font-semibold text-xs">Meta Ad Account ID (Pengelola Iklan)</Label>
                    <Input
                      placeholder="Contoh: 779514711733398"
                      value={pixelConfig.metaAdAccountId || '779514711733398'}
                      onChange={e => setPixelConfig({ ...pixelConfig, metaAdAccountId: e.target.value })}
                      className="bg-white border-slate-200 text-xs font-mono h-10 rounded-xl"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-slate-700 font-semibold text-xs">Meta Pixel ID (Facebook / IG)</Label>
                    <Input
                      value={pixelConfig.metaPixelId}
                      onChange={e => setPixelConfig({ ...pixelConfig, metaPixelId: e.target.value })}
                      className="bg-white border-slate-200 text-xs font-mono h-10 rounded-xl"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-slate-700 font-semibold text-xs">TikTok Pixel ID</Label>
                    <Input
                      value={pixelConfig.tikTokPixelId}
                      onChange={e => setPixelConfig({ ...pixelConfig, tikTokPixelId: e.target.value })}
                      className="bg-white border-slate-200 text-xs font-mono h-10 rounded-xl"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-slate-700 font-semibold text-xs">Google Tag Manager (GTM) ID</Label>
                    <Input
                      value={pixelConfig.googleTagId}
                      onChange={e => setPixelConfig({ ...pixelConfig, googleTagId: e.target.value })}
                      className="bg-white border-slate-200 text-xs font-mono h-10 rounded-xl"
                    />
                  </div>

                  <Button type="submit" className="bg-[#8C6208] hover:bg-[#724F06] text-white font-bold h-10 px-6 rounded-xl text-xs shadow-sm">
                    Simpan Perubahan
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

      </main>

      {/* ─── MODAL: TAMBAH LEAD BARU ─── */}
      <Dialog open={isAddLeadOpen} onOpenChange={setIsAddLeadOpen}>
        <DialogContent className="bg-white sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-900">Tambah Lead Prospect Baru</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateLead} className="space-y-3 text-xs pt-2">
            <div className="space-y-1">
              <Label className="text-slate-700 font-semibold">Nama Lead / Perusahaan *</Label>
              <Input
                required
                placeholder="Contoh: PT Harum Wangi"
                value={newLead.name}
                onChange={e => setNewLead({ ...newLead, name: e.target.value })}
                className="h-9 text-xs rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-700 font-semibold">Email Client</Label>
                <Input
                  type="email"
                  placeholder="client@email.com"
                  value={newLead.email}
                  onChange={e => setNewLead({ ...newLead, email: e.target.value })}
                  className="h-9 text-xs rounded-xl"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-slate-700 font-semibold">No. WhatsApp *</Label>
                <Input
                  required
                  placeholder="0812-3456-7890"
                  value={newLead.phone}
                  onChange={e => setNewLead({ ...newLead, phone: e.target.value })}
                  className="h-9 text-xs rounded-xl"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-700 font-semibold">Kota / Asal</Label>
                <Input
                  placeholder="Jakarta"
                  value={newLead.city}
                  onChange={e => setNewLead({ ...newLead, city: e.target.value })}
                  className="h-9 text-xs rounded-xl"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-slate-700 font-semibold">Sumber Lead</Label>
                <Select value={newLead.source} onValueChange={val => setNewLead({ ...newLead, source: val })}>
                  <SelectTrigger className="h-9 text-xs rounded-xl">
                    <SelectValue placeholder="Sumber" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Google Ads">Google Ads</SelectItem>
                    <SelectItem value="Meta Ads">Meta Ads</SelectItem>
                    <SelectItem value="TikTok Ads">TikTok Ads</SelectItem>
                    <SelectItem value="Website">Website</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-slate-700 font-semibold">Paket Maklon Diminati</Label>
              <Input
                placeholder="Parfum Signature 50ml"
                value={newLead.package}
                onChange={e => setNewLead({ ...newLead, package: e.target.value })}
                className="h-9 text-xs rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-700 font-semibold">Target MOQ</Label>
                <Input
                  placeholder="1.000 Pcs"
                  value={newLead.moq}
                  onChange={e => setNewLead({ ...newLead, moq: e.target.value })}
                  className="h-9 text-xs rounded-xl"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-slate-700 font-semibold">PIC Marketing</Label>
                <Select value={newLead.pic} onValueChange={val => setNewLead({ ...newLead, pic: val })}>
                  <SelectTrigger className="h-9 text-xs rounded-xl">
                    <SelectValue placeholder="PIC" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Fadli">Fadli</SelectItem>
                    <SelectItem value="Dinda">Dinda</SelectItem>
                    <SelectItem value="Andi">Andi</SelectItem>
                    <SelectItem value="Budi">Budi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" onClick={() => setIsAddLeadOpen(false)} className="h-9 text-xs rounded-xl">
                Batal
              </Button>
              <Button type="submit" className="bg-[#8C6208] hover:bg-[#724F06] text-white font-bold h-9 text-xs rounded-xl">
                Simpan Lead
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── MODAL: TAMBAH ADMIN WA BARU ─── */}
      <Dialog open={isAddAdminOpen} onOpenChange={setIsAddAdminOpen}>
        <DialogContent className="bg-white sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#8C6208]" />
              Tambah Admin WhatsApp Sales / CS
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateAdmin} className="space-y-4 text-xs pt-2">
            <div className="space-y-1.5">
              <Label className="text-slate-700 font-semibold">Nama Admin CS / Sales *</Label>
              <Input
                required
                placeholder="Contoh: Fadli / Dinda"
                value={newAdminForm.name}
                onChange={e => setNewAdminForm({ ...newAdminForm, name: e.target.value })}
                className="h-10 text-xs rounded-xl"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-700 font-semibold">Nomor WhatsApp (Format: 628... / 08...) *</Label>
              <Input
                required
                placeholder="Contoh: 6285191626987"
                value={newAdminForm.phone}
                onChange={e => setNewAdminForm({ ...newAdminForm, phone: e.target.value })}
                className="h-10 text-xs font-mono rounded-xl"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-700 font-semibold">Jabatan / Role</Label>
              <Input
                placeholder="Contoh: Admin Sales 1 / Senior CS"
                value={newAdminForm.role}
                onChange={e => setNewAdminForm({ ...newAdminForm, role: e.target.value })}
                className="h-10 text-xs rounded-xl"
              />
            </div>

            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" onClick={() => setIsAddAdminOpen(false)} className="h-9 text-xs rounded-xl">
                Batal
              </Button>
              <Button type="submit" className="bg-[#8C6208] hover:bg-[#724F06] text-white font-bold h-9 text-xs rounded-xl">
                Simpan Admin WA
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── MODAL: EDIT ADMIN WA ─── */}
      <Dialog open={!!editingAdmin} onOpenChange={(open) => !open && setEditingAdmin(null)}>
        <DialogContent className="bg-white sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-[#8C6208]" />
              Edit Data Admin WhatsApp
            </DialogTitle>
          </DialogHeader>

          {editingAdmin && (
            <form onSubmit={handleUpdateAdmin} className="space-y-4 text-xs pt-2">
              <div className="space-y-1.5">
                <Label className="text-slate-700 font-semibold">Nama Admin CS / Sales *</Label>
                <Input
                  required
                  placeholder="Contoh: Fadli / Dinda"
                  value={editingAdmin.name}
                  onChange={e => setEditingAdmin({ ...editingAdmin, name: e.target.value })}
                  className="h-10 text-xs rounded-xl"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-700 font-semibold">Nomor WhatsApp (Format: 628... / 08...) *</Label>
                <Input
                  required
                  placeholder="Contoh: 6285191626987"
                  value={editingAdmin.phone}
                  onChange={e => setEditingAdmin({ ...editingAdmin, phone: e.target.value })}
                  className="h-10 text-xs font-mono rounded-xl"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-700 font-semibold">Jabatan / Role</Label>
                <Input
                  placeholder="Contoh: Admin Sales 1 / Senior CS"
                  value={editingAdmin.role}
                  onChange={e => setEditingAdmin({ ...editingAdmin, role: e.target.value })}
                  className="h-10 text-xs rounded-xl"
                />
              </div>

              <DialogFooter className="pt-3">
                <Button type="button" variant="outline" onClick={() => setEditingAdmin(null)} className="h-9 text-xs rounded-xl">
                  Batal
                </Button>
                <Button type="submit" className="bg-[#8C6208] hover:bg-[#724F06] text-white font-bold h-9 text-xs rounded-xl">
                  Simpan Perubahan
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── MODAL: KONFIRMASI CLOSING & INVOICE DEAL ─── */}
      <Dialog open={!!closingModalLead} onOpenChange={(open) => !open && setClosingModalLead(null)}>
        <DialogContent className="bg-white sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Konfirmasi Closing & Input Nilai Invoice Klien
            </DialogTitle>
          </DialogHeader>

          {closingModalLead && (
            <form onSubmit={handleSaveClosingDeal} className="space-y-4 text-xs pt-2">
              <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-200 text-emerald-900 space-y-1">
                <p className="font-bold">Klien: {closingModalLead.name}</p>
                <p className="text-[11px] text-emerald-700">Sumber: {closingModalLead.source} • PIC: {closingModalLead.pic}</p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-700 font-semibold">Total Nilai Deal / Invoice Klien (Rp) *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 font-bold font-mono text-slate-500">Rp</span>
                  <Input
                    required
                    type="number"
                    placeholder="Contoh: 50000000"
                    value={closingInvoiceAmount}
                    onChange={e => setClosingInvoiceAmount(e.target.value)}
                    className="pl-10 h-10 text-xs font-mono font-bold rounded-xl"
                  />
                </div>
                <p className="text-[10px] text-slate-500">Nilai invoice ini akan otomatis digunakan untuk kalkulasi ROAS dan Total Omset Realtime.</p>
              </div>

              <DialogFooter className="pt-3">
                <Button type="button" variant="outline" onClick={() => setClosingModalLead(null)} className="h-9 text-xs rounded-xl">
                  Batal
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-9 text-xs rounded-xl gap-1">
                  <Check className="w-4 h-4" /> Simpan Deal Closing
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── MODAL: TAMBAH CAMPAIGN IKLAN ─── */}
      <Dialog open={isAddCampaignOpen} onOpenChange={setIsAddCampaignOpen}>
        <DialogContent className="bg-white sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#8C6208]" />
              Tambah Campaign Iklan Baru
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateCampaign} className="space-y-4 text-xs pt-2">
            <div className="space-y-1.5">
              <Label className="text-slate-700 font-semibold">Nama Campaign Iklan *</Label>
              <Input
                required
                placeholder="Contoh: Prospek Meta Ads Parfum 06/08"
                value={newCampaignForm.name}
                onChange={e => setNewCampaignForm({ ...newCampaignForm, name: e.target.value })}
                className="h-10 text-xs rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-700 font-semibold">Channel Iklan</Label>
                <Select value={newCampaignForm.channel} onValueChange={val => setNewCampaignForm({ ...newCampaignForm, channel: val })}>
                  <SelectTrigger className="h-10 text-xs rounded-xl">
                    <SelectValue placeholder="Channel" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Meta Ads (FB/IG)">Meta Ads (FB/IG)</SelectItem>
                    <SelectItem value="Google Ads">Google Ads</SelectItem>
                    <SelectItem value="TikTok Ads">TikTok Ads</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-700 font-semibold">Status Campaign</Label>
                <Select value={newCampaignForm.status} onValueChange={val => setNewCampaignForm({ ...newCampaignForm, status: val })}>
                  <SelectTrigger className="h-10 text-xs rounded-xl">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Aktif">Aktif</SelectItem>
                    <SelectItem value="Pause">Pause</SelectItem>
                    <SelectItem value="Selesai">Selesai</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-700 font-semibold">Budget Harian (Rp)</Label>
                <Input
                  type="number"
                  placeholder="50000"
                  value={newCampaignForm.budget}
                  onChange={e => setNewCampaignForm({ ...newCampaignForm, budget: e.target.value })}
                  className="h-10 text-xs font-mono rounded-xl"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-700 font-semibold">Spent Terpakai (Rp)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={newCampaignForm.spent}
                  onChange={e => setNewCampaignForm({ ...newCampaignForm, spent: e.target.value })}
                  className="h-10 text-xs font-mono rounded-xl"
                />
              </div>
            </div>

            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" onClick={() => setIsAddCampaignOpen(false)} className="h-9 text-xs rounded-xl">
                Batal
              </Button>
              <Button type="submit" className="bg-[#8C6208] hover:bg-[#724F06] text-white font-bold h-9 text-xs rounded-xl">
                Simpan Campaign
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
